<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Update Allocation</h1>
      <p class="subtitle">
        Record {{ unitLabel }} dispensed for {{ ticketItem.productName || 'this item' }}.
      </p>
    </div>
    <div class="modal-card-body">
      <div class="amount-info">
        <div class="amount-info-head">
          <span class="amount-label">Remaining</span>
          <span class="amount-value" :class="{ 'amount-over': remaining < 0 }">
            {{ remaining }} / {{ totalAllocation }} {{ unitLabel }}
          </span>
        </div>
        <div class="progress-bar" v-if="totalAllocation">
          <div
            class="progress-fill"
            :class="{ 'progress-fill-over': totalUsed > totalAllocation }"
            :style="{ width: progressPercent + '%' }"
          ></div>
        </div>
      </div>

      <div class="form-grid">
        <div class="form-row">
          <label class="form-label">Date Dispensed</label>
          <DateInput
            :modelValue="dateDispensed"
            @update:modelValue="dateDispensed = $event"
          />
        </div>
        <div class="form-row">
          <label class="form-label">{{ unitLabelCapitalized }} Dispensed</label>
          <NumericalInput v-model="amountUsed" placeholder="0" />
          <p v-if="overAllocationWarning" class="form-hint form-hint-warn">
            This would put total {{ unitLabel }} used over the allocated amount.
          </p>
        </div>
        <div class="form-row">
          <label class="form-label">Note (optional)</label>
          <TextInput v-model="note" :placeholder="'e.g. Visit #3'" />
        </div>
      </div>

      <div class="history-section">
        <div class="history-header">
          <h2 class="history-title">History</h2>
          <span v-if="events.length > 0" class="history-count">{{ events.length }}</span>
        </div>

        <div v-if="isLoading" class="history-empty">Loading...</div>
        <div v-else-if="events.length === 0" class="history-empty">
          No {{ unitLabel }} recorded yet.
        </div>
        <ul v-else class="history-list">
          <li v-for="event in events" :key="event.distributionEventId" class="history-item">
            <template v-if="editingId === event.distributionEventId">
              <div class="history-edit">
                <div class="history-edit-fields">
                  <div class="record-field">
                    <label>Date</label>
                    <DateInput
                      :modelValue="editDraft.dateDispensed"
                      @update:modelValue="editDraft.dateDispensed = $event"
                    />
                  </div>
                  <div class="record-field">
                    <label>{{ unitLabelCapitalized }}</label>
                    <NumericalInput v-model="editDraft.amount" placeholder="0" />
                  </div>
                  <div class="record-field record-field-grow">
                    <label>Note</label>
                    <TextInput v-model="editDraft.note" placeholder="Optional" />
                  </div>
                </div>
                <div class="history-edit-actions">
                  <button class="button" @click="cancelEdit">Cancel</button>
                  <button class="button primary" @click="saveEdit(event)" :disabled="!canSaveEdit">Save</button>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="history-left">
                <div class="history-amount">
                  {{ event.amount }}
                  <span class="history-unit">{{ unitLabel }}</span>
                </div>
                <div class="history-meta">
                  {{ formatDate(event.dateDispensed) }} · {{ event.recordedByUserName || 'Unknown' }}
                </div>
                <div v-if="event.note" class="history-note">{{ event.note }}</div>
              </div>
              <div class="history-actions">
                <button class="icon-button" @click="startEdit(event)" title="Edit">
                  <PencilIcon :size="16" />
                </button>
                <button class="icon-button" @click="handleDelete(event)" title="Delete">
                  <TrashIcon :size="16" />
                </button>
              </div>
            </template>
          </li>
        </ul>
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button secondary form-button close-on-click" @click="handleCancel">
        Cancel
      </button>
      <button class="button primary form-button" @click="handleSubmit" :disabled="!canSubmit || isSubmitting">
        {{ isSubmitting ? 'Saving...' : 'Save' }}
      </button>
    </div>
    <div v-if="error" class="modal-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import PencilIcon from 'vue-material-design-icons/Pencil.vue';
import TrashIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import NumericalInput from '@/components/inputs/NumericalInput.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import DateInput from '@/components/inputs/DateInput.vue';
import { queryClient } from '@/libs/queryClient';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { getLocalToday } from '@/utils/dateUtils';
import { alertService } from '@/libs/alert';

export default {
  name: 'UpdateAllocationModal',
  props: {
    ticketItem: { type: Object, required: true }
  },
  emits: ['close', 'updated'],
  components: { CloseIcon, PencilIcon, TrashIcon, NumericalInput, TextInput, DateInput },
  data() {
    return {
      events: [],
      isLoading: false,
      isSubmitting: false,
      amountUsed: null,
      note: '',
      dateDispensed: getLocalToday(),
      editingId: null,
      editDraft: { amount: null, note: '', dateDispensed: '' },
      error: null
    };
  },
  computed: {
    unitLabel() {
      return (this.ticketItem?.allowanceUnit || 'units').toLowerCase();
    },
    unitLabelCapitalized() {
      const u = this.unitLabel;
      return u.charAt(0).toUpperCase() + u.slice(1);
    },
    totalAllocation() {
      const item = this.ticketItem;
      if (!item?.allowanceEnabled || !item.allowanceAmount || !item.quantity) return 0;
      return item.allowanceAmount * item.quantity;
    },
    totalUsed() {
      return this.events.reduce((sum, e) => sum + (e.amount || 0), 0);
    },
    remaining() {
      return this.totalAllocation - this.totalUsed;
    },
    progressPercent() {
      if (!this.totalAllocation) return 0;
      return Math.min(100, Math.round((this.totalUsed / this.totalAllocation) * 100));
    },
    canSubmit() {
      return this.amountUsed && this.amountUsed > 0 && !!this.dateDispensed;
    },
    canSaveEdit() {
      return this.editDraft.amount && this.editDraft.amount > 0 && !!this.editDraft.dateDispensed;
    },
    overAllocationWarning() {
      if (!this.totalAllocation || !this.amountUsed) return false;
      return (this.totalUsed + Number(this.amountUsed)) > this.totalAllocation;
    }
  },
  async created() {
    await this.loadEvents();
  },
  methods: {
    formatDate: formatUtils.formatDate,
    async loadEvents() {
      const ticketItemId = this.ticketItem.ticketItemId;
      if (!ticketItemId) return;
      this.isLoading = true;
      try {
        const response = await queryClient.fetchQuery({
          queryKey: ['distribution-events', { ticketItemId }],
          queryFn: () => api.fetchDistributionEvents({ ticketItemId })
        });
        this.events = response.distributionEvents || [];
      } catch (err) {
        console.error('Failed to load distribution events:', err);
        this.error = 'Failed to load history: ' + err.message;
      } finally {
        this.isLoading = false;
      }
    },
    invalidate() {
      queryClient.invalidateQueries({ queryKey: ['distribution-events'] });
      // Allowance remaining is derived from dispensed doses, so drop the ticket
      // list/search cache to reflect the new count
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    handleCancel() {
      // Closed via close-on-click
    },
    async handleSubmit() {
      this.error = null;
      if (!this.canSubmit || this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        const created = await api.createDistributionEvent({
          ticketItemId: this.ticketItem.ticketItemId,
          amount: Number(this.amountUsed),
          note: this.note || null,
          dateDispensed: this.dateDispensed
        });
        this.events = [created, ...this.events];
        this.amountUsed = null;
        this.note = '';
        this.dateDispensed = getLocalToday();
        this.invalidate();
        this.$emit('updated');
        const closeButton = this.$el.querySelector('.close-on-click');
        if (closeButton) closeButton.click();
      } catch (err) {
        console.error('Failed to save distribution event:', err);
        this.error = err.message || 'Failed to save update';
      } finally {
        this.isSubmitting = false;
      }
    },
    startEdit(event) {
      this.editingId = event.distributionEventId;
      this.editDraft = {
        amount: event.amount,
        note: event.note || '',
        dateDispensed: event.dateDispensed
      };
    },
    cancelEdit() {
      this.editingId = null;
      this.editDraft = { amount: null, note: '', dateDispensed: '' };
    },
    async saveEdit(event) {
      if (!this.canSaveEdit) return;
      try {
        const updated = await api.updateDistributionEvent(event.distributionEventId, {
          amount: Number(this.editDraft.amount),
          note: this.editDraft.note || null,
          dateDispensed: this.editDraft.dateDispensed
        });
        this.events = this.events.map(e =>
          e.distributionEventId === updated.distributionEventId ? updated : e
        );
        this.cancelEdit();
        this.invalidate();
        this.$emit('updated');
      } catch (err) {
        console.error('Failed to update distribution event:', err);
        alertService.error('Failed to update: ' + err.message);
      }
    },
    async handleDelete(event) {
      if (!confirm(`Delete this record of ${event.amount} ${this.unitLabel}?`)) return;
      try {
        await api.deleteDistributionEvent(event.distributionEventId);
        this.events = this.events.filter(e => e.distributionEventId !== event.distributionEventId);
        this.invalidate();
        this.$emit('updated');
      } catch (err) {
        console.error('Failed to delete distribution event:', err);
        alertService.error('Failed to delete: ' + err.message);
      }
    }
  }
};
</script>

<style scoped>
.modal-card {
  max-width: 480px;
}

.subtitle {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  font-weight: 400;
}

.amount-info {
  padding: 12px;
  background-color: #f9fafb;
  border-radius: var(--border-radius);
  border: 1px solid #e5e7eb;
  margin-bottom: 16px;
}

.amount-info-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 10px;
}

.amount-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.amount-value {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  font-variant-numeric: tabular-nums;
}

.amount-over {
  color: #b91c1c;
}

.progress-bar {
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--primary-color);
  border-radius: 999px;
  transition: width 0.2s ease;
}

.progress-fill-over {
  background: #b91c1c;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  font-style: italic;
}

.form-hint-warn {
  color: #92400e;
  font-style: normal;
}

.history-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.history-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.history-title {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.history-count {
  font-size: 11px;
  background: #e5e7eb;
  color: #4b5563;
  padding: 1px 7px;
  border-radius: 999px;
  font-weight: 600;
}

.history-empty {
  padding: 20px 16px;
  background: #fafbfc;
  border: 1px dashed #e5e7eb;
  border-radius: var(--border-radius);
  color: #9ca3af;
  font-size: 13px;
  text-align: center;
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: var(--border-radius);
  gap: 10px;
}

.history-left {
  flex: 1;
  min-width: 0;
}

.history-amount {
  font-weight: 600;
  color: #111827;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

.history-unit {
  font-weight: 500;
  color: #6b7280;
  font-size: 13px;
  margin-left: 2px;
}

.history-meta {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.history-note {
  font-size: 13px;
  color: #374151;
  margin-top: 4px;
}

.history-actions {
  display: flex;
  gap: 2px;
}

.icon-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9ca3af;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.icon-button:hover {
  background: #f3f4f6;
  color: #111827;
}

.history-edit {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.history-edit-fields {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.history-edit-fields .record-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 0 auto;
}

.history-edit-fields .record-field label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
}

.history-edit-fields .record-field-grow {
  flex: 1 1 140px;
  min-width: 140px;
}

.history-edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.modal-card-footer {
  display: flex;
  gap: 16px;
}
</style>
